import { useCallback } from 'react';
import {
  UseDiningPrimaryActionCommandParams,
  UseDiningPrimaryActionCommandResult,
} from '@msc-b2c/interfaces/interfaces';
import { DiningReservationMode, DiningStepId } from '@msc-b2c/interfaces/enums';

/**
 * Encapsulates the footer primary CTA command logic.
 *
 * Keeps the drawer component focused on orchestration/rendering while this hook
 * owns step-specific branching for apply/confirm behavior.
 */
export const useDiningPrimaryActionCommand = ({
  isFooterDrivenDateTimeStep,
  handleConfirmDateTimeSelection,
  currentStep,
  reservationMode,
  setIsProcessing,
  onConfirmPassengers,
  passengersToAdd,
  getDiningSelectionForCart,
  onApply,
  setIsConfirmationDrawer,
  maximumSlotsBookable,
  selectedRestaurantsCount,
  setIsDiningButtonLoading,
  handlePaxSelectionNext,
  onPaxSelectionPersisted,
  setEditedDiningPackage,
}: UseDiningPrimaryActionCommandParams): UseDiningPrimaryActionCommandResult => {
  const handlePrimary = useCallback(async () => {
    if (
      isFooterDrivenDateTimeStep ||
      (currentStep === DiningStepId.DATE_SELECTION &&
        reservationMode === DiningReservationMode.WITH_TIMESLOT)
    ) {
      await handleConfirmDateTimeSelection();
      return;
    }

    if (
      currentStep === DiningStepId.CHOOSE_WHEN_BOOK &&
      reservationMode === DiningReservationMode.WITHOUT_TIMESLOT
    ) {
      setIsProcessing(true);
      onConfirmPassengers(passengersToAdd ?? {}, DiningStepId.CONFIRM, getDiningSelectionForCart());
      onApply();
      setIsConfirmationDrawer();
      return;
    }

    if (currentStep === DiningStepId.RESERVATION_RECAP) {
      const isIncompleteSingleSlotSelection =
        maximumSlotsBookable <= 1 && selectedRestaurantsCount < maximumSlotsBookable;

      if (isIncompleteSingleSlotSelection) {
        return;
      }
      setIsProcessing(true);
      onConfirmPassengers(passengersToAdd ?? {}, currentStep, getDiningSelectionForCart());
      onApply();
      setIsConfirmationDrawer();
      return;
    }

    if (currentStep === DiningStepId.PAX_SELECTION_EDIT) {
      setIsDiningButtonLoading(true);
      onConfirmPassengers(passengersToAdd ?? {}, currentStep, getDiningSelectionForCart());
      onPaxSelectionPersisted?.(passengersToAdd ?? null);
      handlePaxSelectionNext();
      setIsDiningButtonLoading(false);
      setEditedDiningPackage();
      return;
    }

    if (setEditedDiningPackage) {
      setEditedDiningPackage();
    }
    setIsProcessing(true);
    onConfirmPassengers(passengersToAdd ?? {}, currentStep, getDiningSelectionForCart());
    onApply();
    setIsConfirmationDrawer();
  }, [
    currentStep,
    getDiningSelectionForCart,
    handleConfirmDateTimeSelection,
    isFooterDrivenDateTimeStep,
    maximumSlotsBookable,
    onApply,
    onConfirmPassengers,
    passengersToAdd,
    reservationMode,
    selectedRestaurantsCount,
    onPaxSelectionPersisted,
    setIsConfirmationDrawer,
    setIsDiningButtonLoading,
    setIsProcessing,
    handlePaxSelectionNext,
  ]);

  return { handlePrimary };
};
